import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import OlPolygon from 'ol/geom/Polygon';
import * as turf from '@turf/turf';
import { latLonToEN } from '@openlayers/ol-utils';

/**
 * Which part of an alignment corridor to include, relative to the direction of
 * travel (increasing station): the full width, only the left half, or only the
 * right half. "Left" is the +90° normal of the centerline tangent.
 */
export type CorridorSide = 'both' | 'left' | 'right';

export function splitOpenlayersFeatureIntoSegments(olFeature, targetSegmentArea): Feature<Polygon>[] {
    // Split openlayers feature into smaller parts such that each part can be processed separately
    const { horizontalLines, verticalLines } = getCutLines(olFeature, targetSegmentArea);
    return getSegments(horizontalLines, verticalLines, olFeature);
}

function getCutLines(olFeature, targetArea) {
    // Get extents of the feature
    const olGeom = olFeature.getGeometry();
    const [minX, minY, maxX, maxY] = olGeom.getExtent();

    // Side length in meters
    const sideLength = Math.sqrt(targetArea);

    // Create the necessary variables
    const horizontalLines = [];
    const verticalLines = [];

    for (let y = minY; y < maxY; y += sideLength) {
        const line = turf.lineString([
            [minX, y],
            [maxX, y],
        ]);
        horizontalLines.push(line);
    }

    for (let x = minX; x < maxX; x += sideLength) {
        const line = turf.lineString([
            [x, minY],
            [x, maxY],
        ]);
        verticalLines.push(line);
    }

    return { horizontalLines, verticalLines };
}

function getSegments(horizontalLines, verticalLines, olFeature: Feature<Polygon>): Feature<Polygon>[] {
    // Generate turf polygon from the openlayers feature.
    // Projections must be explicit: OL features are in EPSG:3857; turf expects WGS84 (EPSG:4326).
    const geoJsonFormat = new GeoJSON();
    const currentTurfPolygon = geoJsonFormat.writeFeatureObject(olFeature, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326',
    });

    // Necessary variables
    let polygonsToProcess = [currentTurfPolygon];
    let nextIterationPolygons = [];
    const splitSegments = [];

    // Apply horizontal cuts iteratively
    horizontalLines.forEach((line, index) => {
        nextIterationPolygons = []; // Reset for this iteration
        polygonsToProcess.forEach(poly => {
            // Cut each existing polygon with the current horizontal line
            const cutResult = polygonCut(poly, line, `h_cut_${index}`);
            if (cutResult && cutResult.features) {
                cutResult.features.forEach(f => {
                    if (f.geometry && f.geometry.type === 'Polygon') {
                        nextIterationPolygons.push(f);
                    }
                });
            } else {
                nextIterationPolygons.push(poly);
            }
        });
        polygonsToProcess = nextIterationPolygons;
    });

    // Apply vertical cuts to the polygons that resulted from horizontal cuts
    verticalLines.forEach((line, index) => {
        nextIterationPolygons = [];
        polygonsToProcess.forEach(poly => {
            const cutResult = polygonCut(poly, line, `v_cut_${index}`);
            if (cutResult && cutResult.features) {
                cutResult.features.forEach(f => {
                    if (f.geometry && f.geometry.type === 'Polygon') {
                        nextIterationPolygons.push(f);
                    }
                });
            } else {
                nextIterationPolygons.push(poly);
            }
        });
        polygonsToProcess = nextIterationPolygons;
    });

    // Convert back to OpenLayers features. Projections must match the write step above.
    polygonsToProcess.forEach(segmentTurf => {
        const olSegment = geoJsonFormat.readFeature(segmentTurf, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
        }) as Feature<Polygon>;
        splitSegments.push(olSegment);
    });

    return splitSegments;
}

function polygonCut(polygon, line, idPrefix: string) {
    const THICK_LINE_UNITS = 'kilometers';
    const THICK_LINE_WIDTH = 0.0001; // 0.001 km = 0.1 meter on each side.
    const cutPolyGeoms = [];
    const cutFeatures = [];
    const offsetLine = [];

    const intersectPoints = turf.lineIntersect(polygon, line);
    if (intersectPoints.features.length === 0) {
        console.error('No intersection points found between polygon and line.');
        return null;
    }

    const lineCoords = turf.getCoords(line);
    // Check if the line's endpoints are fully contained within the polygon.
    // If so, the line doesn't "cut through" in the typical sense of splitting into separate parts.
    if (turf.booleanWithin(turf.point(lineCoords[0]), polygon) || turf.booleanWithin(turf.point(lineCoords[lineCoords.length - 1]), polygon)) {
        return null;
    }

    try {
        offsetLine[0] = turf.lineOffset(line, THICK_LINE_WIDTH, { units: THICK_LINE_UNITS });
        offsetLine[1] = turf.lineOffset(line, -THICK_LINE_WIDTH, { units: THICK_LINE_UNITS });
    } catch (e) {
        console.error('Error creating offset lines:', e);
        return null;
    }

    const thickLineCoords0 = turf.getCoords(offsetLine[0]);
    const thickLineCoords1 = turf.getCoords(offsetLine[1]);

    // To form a closed polygon from the two offset lines:
    // Take coordinates from the first offset line, then reverse coordinates from the second,
    // and finally close the ring with the first coordinate of the first offset line.
    const combinedCoords = [...thickLineCoords0, ...[...thickLineCoords1].reverse(), thickLineCoords0[0]];

    const thickLinePolygon = turf.polygon([combinedCoords]);

    // Perform the difference operation: subtract the thick line polygon from the original polygon.
    const clipped = turf.difference(turf.featureCollection([polygon, thickLinePolygon]));

    // If `clipped` is null, it means the original polygon was entirely removed or no valid geometry resulted.
    if (clipped === null) {
        console.error('No valid geometry resulted from the difference operation.');
        return null;
    }

    // Process the result, which can be a Polygon or MultiPolygon.
    if (clipped.geometry.type === 'Polygon') {
        cutPolyGeoms.push(clipped.geometry);
    } else if (clipped.geometry.type === 'MultiPolygon') {
        // If it's a MultiPolygon, iterate through its individual polygon geometries.
        for (let k = 0; k < clipped.geometry.coordinates.length; k++) {
            cutPolyGeoms.push(turf.polygon(clipped.geometry.coordinates[k]).geometry);
        }
    } else {
        console.error('Unexpected geometry type returned by turf.difference:');
        return null;
    }

    // Create new Feature objects for each cut polygon.
    for (let k = 0; k < cutPolyGeoms.length; k++) {
        const id = `${idPrefix}_${k}`;
        cutFeatures.push(turf.feature(cutPolyGeoms[k], { id: id }));
    }

    return turf.featureCollection(cutFeatures);
}

export async function alignmentToPoints(alignment: IAlignment, startStation: string | number, endStation: string | number, stations) {
    const points = [];
    const alignStartStation = alignment ? alignment.startStation : 0;
    const startIdxFloor = Math.floor((startStation as number) - alignStartStation);
    const endIdxFloor = Math.floor((endStation as number) - alignStartStation);

    // Start point interpolation
    if (startIdxFloor >= 0 && startIdxFloor + 1 < stations.length) {
        const ratio = (startStation as number) - (alignStartStation + startIdxFloor);
        const lon = stations[startIdxFloor].lon + ratio * (stations[startIdxFloor + 1].lon - stations[startIdxFloor].lon);
        const lat = stations[startIdxFloor].lat + ratio * (stations[startIdxFloor + 1].lat - stations[startIdxFloor].lat);
        points.push([lon, lat]);
    } else if (startIdxFloor >= 0 && startIdxFloor < stations.length) {
        points.push([stations[startIdxFloor].lon, stations[startIdxFloor].lat]);
    }

    // Middle points
    for (let i = startIdxFloor + 1; i < endIdxFloor; i++) {
        if (i >= 0 && i < stations.length) {
            points.push([stations[i].lon, stations[i].lat]);
        }
    }

    // End point interpolation
    if (endIdxFloor >= 0 && endIdxFloor + 1 < stations.length) {
        const ratio = (endStation as number) - (alignStartStation + endIdxFloor);
        const lon = stations[endIdxFloor].lon + ratio * (stations[endIdxFloor + 1].lon - stations[endIdxFloor].lon);
        const lat = stations[endIdxFloor].lat + ratio * (stations[endIdxFloor + 1].lat - stations[endIdxFloor].lat);
        points.push([lon, lat]);
    } else if (endIdxFloor >= 0 && endIdxFloor < stations.length) {
        points.push([stations[endIdxFloor].lon, stations[endIdxFloor].lat]);
    }
    return points;
}

/**
 * Splits an alignment corridor into sections of `sectionLength` meters along the centerline.
 *
 * Rather than a generic area-based grid, each section polygon follows the alignment geometry
 * exactly, with cross-section cuts placed every `sectionLength` meters of arc length.
 * This ensures volume calculations have a consistent chainage resolution along the road.
 *
 * @param enPoints     Centerline in the projected metric CRS returned by `latLonToEN`.
 * @param corridorWidth  Full corridor width in the same unit (meters).
 * @param sectionLength  Arc-length interval in meters (default 10 m).
 */
export function splitCorridorByCenterline(
    enPoints: number[][],
    corridorWidth: number,
    sectionLength: number = 10,
    side: CorridorSide = 'both',
): Feature<OlPolygon>[] {
    const sections = splitCenterlineIntoSections(enPoints, sectionLength);
    return sections.map(sectionPolyline => {
        const polygon = corridorFromEnPolyline(sectionPolyline, corridorWidth, side);
        return new Feature({ geometry: polygon });
    });
}

/**
 * Walks `enPoints` and inserts interpolated boundary points every `sectionLength` meters,
 * returning the sub-polylines (one per section).  Each sub-polyline retains all original
 * alignment vertices that fall within the section so curves are preserved.
 */
function splitCenterlineIntoSections(enPoints: number[][], sectionLength: number): number[][][] {
    const sections: number[][][] = [];
    let currentSection: number[][] = [[...enPoints[0]]];
    let distSinceLastBoundary = 0;

    for (let i = 0; i < enPoints.length - 1; i++) {
        const p1 = enPoints[i];
        const p2 = enPoints[i + 1];
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const segLen = Math.hypot(dx, dy);

        if (segLen === 0) continue;

        let posAlongSeg = 0;

        // Place boundary points every sectionLength meters within this segment
        while (distSinceLastBoundary + (segLen - posAlongSeg) >= sectionLength) {
            const distToBoundary = sectionLength - distSinceLastBoundary;
            posAlongSeg += distToBoundary;
            const t = posAlongSeg / segLen;
            const boundaryPt: number[] = [p1[0] + t * dx, p1[1] + t * dy];

            currentSection.push(boundaryPt);
            sections.push(currentSection);
            currentSection = [[...boundaryPt]];
            distSinceLastBoundary = 0;
        }

        // Add the segment endpoint, but only if it wasn't already added as a boundary
        if (posAlongSeg < segLen) {
            currentSection.push([...p2]);
            distSinceLastBoundary += segLen - posAlongSeg;
        }
    }

    // Flush any remaining points as the final (possibly shorter) section
    if (currentSection.length >= 2) {
        sections.push(currentSection);
    }

    return sections;
}

/**
 * Builds a corridor polygon from a sub-polyline in EN coordinates.
 * Uses the same miter-joint logic as `pointsToFeature` so section polygons
 * tile seamlessly with the full corridor outline.
 */
/**
 * Offsets a centerline polyline to its left and right corridor edges using miter
 * joints, also returning the centerline itself. All three share the same vertex
 * count and ordering so they can be stitched into one- or two-sided polygons.
 */
function offsetCorridorBoundaries(
    enPolyline: number[][],
    corridorWidth: number,
): { left: number[][]; right: number[][]; center: number[][] } {
    const half = corridorWidth / 2;
    const left: number[][] = [];
    const right: number[][] = [];
    const center: number[][] = [];

    for (let i = 0; i < enPolyline.length; i++) {
        const pPrev = i > 0 ? enPolyline[i - 1] : null;
        const pCurr = enPolyline[i];
        const pNext = i < enPolyline.length - 1 ? enPolyline[i + 1] : null;

        center.push([...pCurr]);

        let tx = 0,
            ty = 0,
            miterDist = half;

        if (!pPrev && pNext) {
            const dx = pNext[0] - pCurr[0];
            const dy = pNext[1] - pCurr[1];
            const len = Math.hypot(dx, dy);
            if (len > 0) {
                tx = dx / len;
                ty = dy / len;
            }
        } else if (pPrev && !pNext) {
            const dx = pCurr[0] - pPrev[0];
            const dy = pCurr[1] - pPrev[1];
            const len = Math.hypot(dx, dy);
            if (len > 0) {
                tx = dx / len;
                ty = dy / len;
            }
        } else if (pPrev && pNext) {
            const dx1 = pCurr[0] - pPrev[0],
                dy1 = pCurr[1] - pPrev[1];
            const len1 = Math.hypot(dx1, dy1);
            const v1x = len1 > 0 ? dx1 / len1 : 0;
            const v1y = len1 > 0 ? dy1 / len1 : 0;
            const dx2 = pNext[0] - pCurr[0],
                dy2 = pNext[1] - pCurr[1];
            const len2 = Math.hypot(dx2, dy2);
            const v2x = len2 > 0 ? dx2 / len2 : 0;
            const v2y = len2 > 0 ? dy2 / len2 : 0;
            tx = v1x + v2x;
            ty = v1y + v2y;
            const tLen = Math.hypot(tx, ty);
            if (tLen > 0.0001) {
                tx /= tLen;
                ty /= tLen;
                const cosHalf = v1x * tx + v1y * ty;
                if (cosHalf > 0.1) miterDist = half / cosHalf;
            } else {
                tx = -v1y;
                ty = v1x;
            }
        }

        if (tx === 0 && ty === 0) {
            left.push([...pCurr]);
            right.push([...pCurr]);
            continue;
        }

        const nx = -ty;
        const ny = tx;
        left.push([pCurr[0] + nx * miterDist, pCurr[1] + ny * miterDist]);
        right.push([pCurr[0] - nx * miterDist, pCurr[1] - ny * miterDist]);
    }

    return { left, right, center };
}

/**
 * Builds a closed polygon ring for the requested side of the corridor. The inner
 * boundary of a one-sided corridor is the centerline; the full corridor spans
 * edge to edge.
 */
function assembleCorridorRing(
    boundaries: { left: number[][]; right: number[][]; center: number[][] },
    side: CorridorSide,
): number[][] {
    const { left, right, center } = boundaries;
    if (side === 'left') {
        return [...left, ...[...center].reverse(), left[0]];
    }
    if (side === 'right') {
        return [...center, ...[...right].reverse(), center[0]];
    }
    return [...left, ...[...right].reverse(), left[0]];
}

function corridorFromEnPolyline(enPolyline: number[][], corridorWidth: number, side: CorridorSide = 'both'): OlPolygon {
    const boundaries = offsetCorridorBoundaries(enPolyline, corridorWidth);
    return new OlPolygon([assembleCorridorRing(boundaries, side)]);
}

export async function pointsToFeature(points: number[][], corridorWidth: number, side: CorridorSide = 'both') {
    // Convert to Easting/Northing first (which are in meters, Cartesian system),
    // then reuse the shared corridor geometry so the drawn area, the cut/fill
    // sections and the cross-section line all agree on the selected side.
    const enPoints = points.map(p => latLonToEN(p[1], p[0]));
    return new Feature({ geometry: corridorFromEnPolyline(enPoints, corridorWidth, side) });
}
